import {
  CODEX_REASONING_EFFORT_OPTIONS,
  DEFAULT_MODEL_BY_PROVIDER,
  MODEL_OPTIONS_BY_PROVIDER,
} from "@t3tools/contracts";
import { resolveNewThreadModel } from "../../appSettings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  CalendarClockIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  RotateCwIcon,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import {
  operatorsAutomationsQueryOptions,
  runOperatorsAutomationNowMutationOptions,
  saveOperatorsAutomationMutationOptions,
  setOperatorsAutomationStatusMutationOptions,
} from "../../lib/operatorsAutomationsReactQuery";
import { operatorsActiveSessionsQueryOptions } from "../../lib/operatorsReactQuery";
import type {
  OperatorsAutomation,
  OperatorsAutomationSchedule,
  OperatorsAutomationTarget,
} from "../../operators";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import { cn } from "../../lib/utils";
import { OperatorsSubpageShell } from "../operators/OperatorsSubpageShell";

const WEEKDAYS = [
  { label: "Mon", value: "MO" },
  { label: "Tue", value: "TU" },
  { label: "Wed", value: "WE" },
  { label: "Thu", value: "TH" },
  { label: "Fri", value: "FR" },
  { label: "Sat", value: "SA" },
  { label: "Sun", value: "SU" },
] as const;
const EMPTY_SESSIONS: Array<{ id: string; title: string }> = [];

type FormState = {
  every: number;
  intervalUnit: "minute" | "hour" | "day" | "week";
  model: string;
  name: string;
  prompt: string;
  reasoningEffort: "low" | "medium" | "high" | "xhigh" | "max";
  scheduleType: "interval" | "weekly";
  sessionId: string;
  targetType: "existing_thread" | "new_thread";
  time: string;
  timeZone: string;
  weekdays: Array<"MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU">;
};

const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const emptyForm = (sessionId = ""): FormState => ({
  every: 5,
  intervalUnit: "minute",
  model: DEFAULT_MODEL_BY_PROVIDER.codex,
  name: "",
  prompt: "",
  reasoningEffort: "medium",
  scheduleType: "interval",
  sessionId,
  targetType: "new_thread",
  time: "09:00",
  timeZone: browserTimeZone(),
  weekdays: ["MO"],
});

const formFromAutomation = (automation: OperatorsAutomation): FormState => ({
  every: automation.schedule.type === "interval" ? automation.schedule.every : 5,
  intervalUnit: automation.schedule.type === "interval" ? automation.schedule.unit : "minute",
  model:
    automation.target.type === "new_thread"
      ? resolveNewThreadModel(automation.target.model)
      : DEFAULT_MODEL_BY_PROVIDER.codex,
  name: automation.name,
  prompt: automation.prompt,
  reasoningEffort:
    automation.target.type === "new_thread" ? automation.target.reasoningEffort : "medium",
  scheduleType: automation.schedule.type,
  sessionId: automation.target.type === "existing_thread" ? automation.target.sessionId : "",
  targetType: automation.target.type,
  time: automation.schedule.type === "weekly" ? automation.schedule.time : "09:00",
  timeZone:
    automation.schedule.type === "weekly" ? automation.schedule.timeZone : browserTimeZone(),
  weekdays: automation.schedule.type === "weekly" ? automation.schedule.weekdays : ["MO"],
});

const scheduleLabel = (schedule: OperatorsAutomationSchedule): string => {
  if (schedule.type === "interval") {
    const unit = schedule.every === 1 ? schedule.unit : `${schedule.unit}s`;
    return `Every ${schedule.every} ${unit}`;
  }
  const days = schedule.weekdays
    .map((weekday) => WEEKDAYS.find((item) => item.value === weekday)?.label ?? weekday)
    .join(", ");
  return `${days} at ${schedule.time} (${schedule.timeZone})`;
};

const targetLabel = (automation: OperatorsAutomation, threadNames: Map<string, string>): string => {
  if (automation.target.type === "new_thread") {
    return `New thread · ${automation.target.model} · ${automation.target.reasoningEffort}`;
  }
  return `Existing thread · ${threadNames.get(automation.target.sessionId) ?? automation.target.sessionId}`;
};

const nextRunLabel = (automation: OperatorsAutomation): string => {
  if (automation.status === "archived") return "Archived";
  if (automation.status === "paused") return "Paused";
  if (!automation.nextRunAt) return "Not scheduled";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(automation.nextRunAt));
};

function NativeSelect(props: React.ComponentProps<"select">) {
  return (
    <select
      {...props}
      className={cn(
        "h-8 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/24",
        props.className,
      )}
    />
  );
}

function AutomationEditor(props: {
  automation: OperatorsAutomation | null;
  defaultSessionId: string;
  onClose: () => void;
  open: boolean;
  sessions: Array<{ id: string; title: string }>;
}) {
  const queryClient = useQueryClient();
  const save = useMutation(saveOperatorsAutomationMutationOptions(queryClient));
  const [creationRequestId] = useState(() => crypto.randomUUID());
  const [form, setForm] = useState<FormState>(() =>
    props.automation ? formFromAutomation(props.automation) : emptyForm(props.defaultSessionId),
  );
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const schedule: OperatorsAutomationSchedule =
      form.scheduleType === "interval"
        ? { every: form.every, type: "interval", unit: form.intervalUnit }
        : {
            time: form.time,
            timeZone: form.timeZone.trim(),
            type: "weekly",
            weekdays: form.weekdays,
          };
    const target: OperatorsAutomationTarget =
      form.targetType === "existing_thread"
        ? { sessionId: form.sessionId, type: "existing_thread" }
        : {
            model: form.model,
            reasoningEffort: form.reasoningEffort,
            type: "new_thread",
          };
    try {
      await save.mutateAsync({
        ...(props.automation
          ? { expectedVersion: props.automation.version, id: props.automation.id }
          : { creationRequestId }),
        name: form.name,
        prompt: form.prompt,
        schedule,
        target,
      });
      props.onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Could not save automation.");
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={(open) => !open && props.onClose()}>
      <DialogPopup className="max-w-2xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{props.automation ? "Edit automation" : "Create automation"}</DialogTitle>
            <DialogDescription>
              Run a prompt on a fixed interval or at a weekly local time.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="automation-name">Name</Label>
              <Input
                id="automation-name"
                required
                maxLength={120}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Weekly planning review"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="automation-prompt">Prompt</Label>
              <Textarea
                id="automation-prompt"
                required
                rows={5}
                value={form.prompt}
                onChange={(event) => setForm({ ...form, prompt: event.target.value })}
                placeholder="Review the project and summarize the priorities for this week."
              />
            </div>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">Schedule</legend>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted/50 p-1">
                {(["interval", "weekly"] as const).map((type) => (
                  <Button
                    key={type}
                    type="button"
                    size="sm"
                    variant={form.scheduleType === type ? "secondary" : "ghost"}
                    onClick={() => setForm({ ...form, scheduleType: type })}
                  >
                    {type === "interval" ? "Every interval" : "Weekly time"}
                  </Button>
                ))}
              </div>
              {form.scheduleType === "interval" ? (
                <div className="grid grid-cols-[1fr_1.5fr] gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="automation-every">Every</Label>
                    <Input
                      id="automation-every"
                      type="number"
                      min={1}
                      required
                      value={form.every}
                      onChange={(event) =>
                        setForm({ ...form, every: Number.parseInt(event.target.value, 10) || 1 })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="automation-unit">Unit</Label>
                    <NativeSelect
                      id="automation-unit"
                      value={form.intervalUnit}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          intervalUnit: event.target.value as FormState["intervalUnit"],
                        })
                      }
                    >
                      <option value="minute">Minutes</option>
                      <option value="hour">Hours</option>
                      <option value="day">Days</option>
                      <option value="week">Weeks</option>
                    </NativeSelect>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((weekday) => {
                      const selected = form.weekdays.includes(weekday.value);
                      return (
                        <Button
                          key={weekday.value}
                          type="button"
                          size="xs"
                          variant={selected ? "secondary" : "outline"}
                          aria-pressed={selected}
                          onClick={() => {
                            const weekdays = selected
                              ? form.weekdays.filter((item) => item !== weekday.value)
                              : [...form.weekdays, weekday.value];
                            setForm({ ...form, weekdays });
                          }}
                        >
                          {weekday.label}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="automation-time">Time</Label>
                      <Input
                        id="automation-time"
                        type="time"
                        required
                        value={form.time}
                        onChange={(event) => setForm({ ...form, time: event.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="automation-time-zone">Time zone</Label>
                      <Input
                        id="automation-time-zone"
                        required
                        value={form.timeZone}
                        onChange={(event) => setForm({ ...form, timeZone: event.target.value })}
                        placeholder="America/Sao_Paulo"
                      />
                    </div>
                  </div>
                </div>
              )}
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">Run destination</legend>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted/50 p-1">
                {(["new_thread", "existing_thread"] as const).map((type) => (
                  <Button
                    key={type}
                    type="button"
                    size="sm"
                    variant={form.targetType === type ? "secondary" : "ghost"}
                    onClick={() => setForm({ ...form, targetType: type })}
                  >
                    {type === "new_thread" ? "Start new thread" : "Ping existing thread"}
                  </Button>
                ))}
              </div>
              {form.targetType === "new_thread" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="automation-model">Model</Label>
                    <NativeSelect
                      id="automation-model"
                      value={form.model}
                      onChange={(event) => setForm({ ...form, model: event.target.value })}
                    >
                      {MODEL_OPTIONS_BY_PROVIDER.codex.map((model) => (
                        <option key={model.slug} value={model.slug}>
                          {model.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="automation-reasoning">Reasoning</Label>
                    <NativeSelect
                      id="automation-reasoning"
                      value={form.reasoningEffort}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          reasoningEffort: event.target.value as FormState["reasoningEffort"],
                        })
                      }
                    >
                      {CODEX_REASONING_EFFORT_OPTIONS.map((effort) => (
                        <option key={effort} value={effort}>
                          {effort}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="automation-thread">Thread</Label>
                  <NativeSelect
                    id="automation-thread"
                    required
                    value={form.sessionId}
                    onChange={(event) => setForm({ ...form, sessionId: event.target.value })}
                  >
                    <option value="" disabled>
                      Select a thread
                    </option>
                    {props.sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.title}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              )}
            </fieldset>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                save.isPending ||
                (form.scheduleType === "weekly" && form.weekdays.length === 0) ||
                (form.targetType === "existing_thread" && !form.sessionId)
              }
            >
              {save.isPending ? "Saving…" : props.automation ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

export function AutomationsPage() {
  const queryClient = useQueryClient();
  const automationsQuery = useQuery(operatorsAutomationsQueryOptions());
  const sessionsQuery = useQuery(operatorsActiveSessionsQueryOptions());
  const setStatus = useMutation(setOperatorsAutomationStatusMutationOptions(queryClient));
  const runNow = useMutation(runOperatorsAutomationNowMutationOptions(queryClient));
  const [editor, setEditor] = useState<OperatorsAutomation | "create" | null>(null);
  const automations = automationsQuery.data?.items ?? [];
  const sessions = sessionsQuery.data ?? EMPTY_SESSIONS;
  const threadNames = useMemo(
    () => new Map(sessions.map((session) => [session.id, session.title])),
    [sessions],
  );

  const updateStatus = async (
    automation: OperatorsAutomation,
    status: "active" | "paused" | "archived",
  ) => {
    try {
      await setStatus.mutateAsync({
        expectedVersion: automation.version,
        id: automation.id,
        status,
      });
    } catch (error) {
      toastManager.add({
        description: error instanceof Error ? error.message : "Could not update automation.",
        title: "Automation update failed",
        type: "error",
      });
    }
  };

  const triggerNow = async (automation: OperatorsAutomation) => {
    try {
      await runNow.mutateAsync(automation.id);
      toastManager.add({
        description: `${automation.name} was queued.`,
        title: "Automation started",
        type: "success",
      });
    } catch (error) {
      toastManager.add({
        description: error instanceof Error ? error.message : "Could not start automation.",
        title: "Run failed",
        type: "error",
      });
    }
  };

  return (
    <OperatorsSubpageShell
      header={
        <>
          <CalendarClockIcon className="size-4 text-muted-foreground" />
          <span className="text-sm font-medium">Automations</span>
        </>
      }
      actions={
        <Button size="sm" onClick={() => setEditor("create")}>
          <PlusIcon />
          New automation
        </Button>
      }
      contentClassName="max-w-4xl space-y-6"
    >
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Automations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Schedule prompts to start a new thread or continue one you already own.
        </p>
      </div>

      {automationsQuery.isPending ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading automations…</p>
      ) : automationsQuery.isError ? (
        <Card className="p-6 text-sm text-destructive">Could not load automations.</Card>
      ) : automations.length === 0 ? (
        <Card className="items-center gap-3 px-6 py-12 text-center">
          <CalendarClockIcon className="size-8 text-muted-foreground" />
          <div>
            <p className="font-medium">No automations yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Create one here, or ask Codex in any thread.
            </p>
          </div>
          <Button size="sm" onClick={() => setEditor("create")}>
            Create automation
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {automations.map((automation) => (
            <Card key={automation.id} className="gap-4 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate font-medium">{automation.name}</h2>
                    <Badge
                      variant={
                        automation.status === "active"
                          ? "success"
                          : automation.status === "paused"
                            ? "warning"
                            : "secondary"
                      }
                    >
                      {automation.status}
                    </Badge>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                    {automation.prompt}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Next run
                  </p>
                  <p className="mt-1 text-sm">{nextRunLabel(automation)}</p>
                </div>
              </div>
              <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <p>{scheduleLabel(automation.schedule)}</p>
                <p>{targetLabel(automation, threadNames)}</p>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-border pt-4">
                {automation.status !== "archived" ? (
                  <>
                    <Button size="xs" variant="outline" onClick={() => setEditor(automation)}>
                      <PencilIcon /> Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={runNow.isPending}
                      onClick={() => void triggerNow(automation)}
                    >
                      <RotateCwIcon /> Run now
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={setStatus.isPending}
                      onClick={() =>
                        void updateStatus(
                          automation,
                          automation.status === "paused" ? "active" : "paused",
                        )
                      }
                    >
                      {automation.status === "paused" ? <PlayIcon /> : <PauseIcon />}
                      {automation.status === "paused" ? "Resume" : "Pause"}
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={setStatus.isPending}
                      onClick={() => void updateStatus(automation, "archived")}
                    >
                      <ArchiveIcon /> Archive
                    </Button>
                  </>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editor ? (
        <AutomationEditor
          key={editor === "create" ? "create" : editor.id}
          automation={editor === "create" ? null : editor}
          defaultSessionId={sessions[0]?.id ?? ""}
          onClose={() => setEditor(null)}
          open
          sessions={sessions}
        />
      ) : null}
    </OperatorsSubpageShell>
  );
}
